import { API_BASE_URL } from '@/config/apiBaseUrl';
import { performTokenRefresh } from './api.service';
import api from './api.service';

export type AiSseChunk =
  | { type: 'text'; content: string }
  | { type: 'tool_start'; toolName: string }
  | { type: 'tool_result'; data: unknown }
  | { type: 'action_required'; payload: Record<string, unknown>; tier: number }
  | { type: 'error'; message: string }
  | { type: 'meta'; conversationId?: string }
  | { type: 'grounding_memories'; items: Array<{ id: string; title: string; reasons: string[] }> };

export interface AiChatRequestBody {
  message: string;
  sessionId: string;
  conversationId?: string;
  pageContext?: Record<string, unknown>;
  confirmed?: boolean;
  enableSemanticSearch?: boolean;
}

export type ConversationMode = 'assistant' | 'analyst' | 'executive';

export type AiConversation = {
  id: string;
  title: string;
  pinned: boolean;
  archived: boolean;
  favorite: boolean;
  mode: ConversationMode;
  teamId?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type AiConversationMessage = {
  id: string;
  conversationId: string;
  role: 'user' | 'assistant' | 'system' | 'tool';
  content: string;
  metadata?: Record<string, unknown> | null;
  createdAt: string;
};

export type AiPinnedItem = {
  id: string;
  sourceType: 'kpi' | 'chart' | 'table' | 'report' | 'insight';
  sourceMessageId?: string | null;
  title: string;
  payload: Record<string, unknown>;
  dashboardTab?: string | null;
  sortOrder: number;
  createdAt: string;
};

export type AiSavedReport = {
  id: string;
  reportName: string;
  description?: string | null;
  query?: string | null;
  intent?: string | null;
  filters?: Record<string, unknown> | null;
  displayColumns?: string[] | null;
  sourceContext?: Record<string, unknown> | null;
  outputType: string;
  schedule?: Record<string, unknown> | null;
  lastRunAt?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type UdqIntentCatalogItem = {
  id: string;
  label: string;
  shortDescription: string;
  requiredModules: string[];
  filtersSchema?: string[];
};

export type AssistantResponseLike = {
  intent?: string;
  answerText?: string;
  count?: number;
  dataTable?: {
    title?: string;
    columns: Array<{ key: string; label: string; type?: string }>;
    rows: Record<string, unknown>[];
  };
  chartSpec?: unknown;
  kpiCards?: Array<{ label: string; value: number | string; format?: string }>;
  links?: Array<{ label: string; path: string }>;
  suggestedActions?: unknown[];
  exportOptions?: string[];
  warnings?: string[];
  sourceContext?: Record<string, unknown>;
};

const DONE = '[DONE]';

function getToken(): string | null {
  return localStorage.getItem('token');
}

async function authorizedFetch(url: string, init: RequestInit): Promise<Response> {
  const tryFetch = (token: string | null) =>
    fetch(url, {
      ...init,
      headers: {
        ...(init.headers || {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

  let res = await tryFetch(getToken());
  if (res.status === 401) {
    try {
      const pair = await performTokenRefresh();
      res = await tryFetch(pair.token);
    } catch {
      throw new Error('Session expired. Please sign in again.');
    }
  }
  return res;
}

export interface AssistantTableExportRequest {
  intent?: string;
  filters?: Record<string, unknown>;
  dataTable?: {
    title?: string;
    columns: Array<{ key: string; label: string; type?: string }>;
    rows: Record<string, unknown>[];
  };
  filenameBase?: string;
}

/**
 * POST /ai-agent/export — download server-generated Excel for an assistant table.
 */
export async function downloadAssistantTableXlsx(body: AssistantTableExportRequest): Promise<void> {
  return downloadAssistantTableExport(body, 'xlsx');
}

export async function downloadAssistantTablePdf(body: AssistantTableExportRequest): Promise<void> {
  return downloadAssistantTableExport(body, 'pdf');
}

async function downloadAssistantTableExport(
  body: AssistantTableExportRequest,
  format: 'xlsx' | 'pdf'
): Promise<void> {
  const url = `${API_BASE_URL}/ai-agent/export`;
  const accept =
    format === 'pdf'
      ? 'application/pdf'
      : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  const res = await authorizedFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: accept },
    body: JSON.stringify({ format, intent: body.intent, filters: body.filters, dataTable: body.dataTable }),
  });
  if (!res.ok) {
    const t = await res.text().catch(() => '');
    let message = t.trim();
    try {
      const parsed = JSON.parse(t) as { message?: string };
      if (parsed.message) message = parsed.message;
    } catch {
      // keep raw text
    }
    throw new Error(message || `Export failed (${res.status})`);
  }
  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition') || '';
  const match = /filename="([^"]+)"/i.exec(disposition);
  const ext = format === 'pdf' ? 'pdf' : 'xlsx';
  const filename = match?.[1] || `${body.filenameBase || 'erp-assistant-export'}.${ext}`;
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(objectUrl);
}

/**
 * POST /ai-agent/chat — parse text/event-stream (data: JSON lines, then [DONE]).
 */
export async function streamAiAgentChat(
  body: AiChatRequestBody,
  onChunk: (c: AiSseChunk) => void,
  options?: { signal?: AbortSignal }
): Promise<void> {
  const url = `${API_BASE_URL}/ai-agent/chat`;
  const tryFetch = (token: string | null) =>
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
      signal: options?.signal,
    });

  let res = await tryFetch(getToken());
  if (res.status === 401) {
    try {
      const pair = await performTokenRefresh();
      res = await tryFetch(pair.token);
    } catch {
      throw new Error('Session expired. Please sign in again.');
    }
  }
  if (!res.ok) {
    const t = await res.text().catch(() => '');
    if (res.status === 429) {
      const hint =
        t && t.length < 500
          ? t
          : 'The server rate limit was hit (often after many other API calls from the same network).';
      throw new Error(
        `Too many requests (429). ${hint} Wait a minute and try again, or ask an admin to raise RATE_LIMIT_MAX_REQUESTS.`
      );
    }
    if (res.status === 502 || res.status === 503) {
      throw new Error(
        `API unavailable (${res.status} Bad Gateway). Nginx could not reach the Node backend. On the server: check PM2 is running, curl http://127.0.0.1:PORT/health, and verify nginx location /api/ proxy_pass matches PORT in backend .env.`
      );
    }
    if (res.status === 504) {
      throw new Error(
        'Gateway timeout (504). The AI request took too long for nginx proxy_read_timeout. Ask an admin to raise proxy_read_timeout for /api/ (e.g. 300s) for SSE endpoints.'
      );
    }
    const trimmed = t.trim();
    const looksLikeHtml = trimmed.startsWith('<') && trimmed.includes('<html');
    throw new Error(
      looksLikeHtml
        ? `AI request failed (${res.status}). Server returned an HTML error page instead of JSON — usually nginx cannot reach the API process.`
        : trimmed || `AI request failed (${res.status})`
    );
  }
  if (!res.body) {
    throw new Error('No response body for AI stream');
  }

  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += dec.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';
    for (const block of parts) {
      for (const line of block.split('\n')) {
        if (!line.startsWith('data: ')) continue;
        const raw = line.slice(6).trim();
        if (raw === DONE) {
          return;
        }
        try {
          const parsed = JSON.parse(raw) as AiSseChunk;
          onChunk(parsed);
        } catch {
          // ignore non-JSON
        }
      }
    }
  }
}

export type AssistantSuggestionsResponse = {
  success: boolean;
  module?: string;
  persona?: string;
  personalized: string[];
  moduleDefaults?: string[];
  favoriteIntents?: Array<{ intent: string; count: number }>;
  dominantPersona?: string | null;
};

export async function fetchAssistantSuggestions(module?: string): Promise<AssistantSuggestionsResponse> {
  const res = await api.getClient().get<AssistantSuggestionsResponse>('/ai-agent/suggestions', {
    params: module ? { module } : undefined,
  });
  return res.data;
}

export async function postChatFeedback(payload: {
  rating: 'helpful' | 'not_helpful' | 'correction';
  userMessage?: string;
  assistantText?: string;
  correction?: string;
  sessionId?: string;
  domain?: string;
}): Promise<{ success: boolean; candidateCreated?: boolean }> {
  const res = await api.getClient().post<{ success: boolean; candidateCreated?: boolean }>(
    '/ai-agent/chat-feedback',
    payload
  );
  return res.data;
}

export type RagMetricsResponse = {
  success: boolean;
  companyId: string;
  days: number;
  metrics: {
    embedding: { sampleCount: number; p95Ms: number; p99Ms: number; maxRowsScanned: number; hitMaxScan: number };
    interactions: { count: number; avgDurationMs: number; p95DurationMs: number };
    flags: Record<string, boolean>;
  };
};

export async function fetchRagMetrics(days = 7): Promise<RagMetricsResponse> {
  const res = await api.getClient().get<RagMetricsResponse>('/ai-agent/rag-metrics', { params: { days } });
  return res.data;
}

export async function fetchConversations(params?: {
  pinned?: boolean;
  archived?: boolean;
  favorite?: boolean;
}): Promise<AiConversation[]> {
  const res = await api.getClient().get<{ success: boolean; conversations: AiConversation[] }>(
    '/ai-agent/conversations',
    { params }
  );
  return res.data.conversations || [];
}

export async function searchConversations(q: string, module?: string): Promise<AiConversation[]> {
  const res = await api.getClient().get<{ success: boolean; conversations: AiConversation[] }>(
    '/ai-agent/conversations/search',
    { params: { q, module } }
  );
  return res.data.conversations || [];
}

export async function createConversation(opts?: {
  title?: string;
  mode?: ConversationMode;
}): Promise<AiConversation> {
  const res = await api.getClient().post<{ success: boolean; conversation: AiConversation }>(
    '/ai-agent/conversations',
    opts || {}
  );
  return res.data.conversation;
}

export async function fetchConversation(id: string): Promise<{
  conversation: AiConversation;
  messages: AiConversationMessage[];
}> {
  const res = await api.getClient().get<{
    success: boolean;
    conversation: AiConversation;
    messages: AiConversationMessage[];
  }>(`/ai-agent/conversations/${id}`);
  return { conversation: res.data.conversation, messages: res.data.messages || [] };
}

export async function patchConversation(
  id: string,
  patch: Partial<{
    title: string;
    pinned: boolean;
    archived: boolean;
    favorite: boolean;
    mode: ConversationMode;
  }>
): Promise<AiConversation> {
  const res = await api.getClient().patch<{ success: boolean; conversation: AiConversation }>(
    `/ai-agent/conversations/${id}`,
    patch
  );
  return res.data.conversation;
}

export async function deleteConversation(id: string): Promise<void> {
  await api.getClient().delete(`/ai-agent/conversations/${id}`);
}

export async function duplicateConversation(id: string): Promise<AiConversation> {
  const res = await api.getClient().post<{ success: boolean; conversation: AiConversation }>(
    `/ai-agent/conversations/${id}/duplicate`
  );
  return res.data.conversation;
}

export async function fetchPins(dashboardTab?: string): Promise<AiPinnedItem[]> {
  const res = await api.getClient().get<{ success: boolean; pins: AiPinnedItem[] }>('/ai-agent/pins', {
    params: dashboardTab ? { dashboardTab } : undefined,
  });
  return res.data.pins || [];
}

export async function createPin(input: {
  sourceType: AiPinnedItem['sourceType'];
  title: string;
  payload: Record<string, unknown>;
  sourceMessageId?: string;
  dashboardTab?: string;
}): Promise<AiPinnedItem> {
  const res = await api.getClient().post<{ success: boolean; pin: AiPinnedItem }>('/ai-agent/pins', input);
  return res.data.pin;
}

export async function deletePin(id: string): Promise<void> {
  await api.getClient().delete(`/ai-agent/pins/${id}`);
}

export async function reorderPins(orderedIds: string[]): Promise<void> {
  await api.getClient().post('/ai-agent/pins/reorder', { orderedIds });
}

export async function fetchSavedReports(): Promise<AiSavedReport[]> {
  const res = await api.getClient().get<{ success: boolean; reports: AiSavedReport[] }>('/ai-agent/reports');
  return res.data.reports || [];
}

export async function fetchSavedReport(id: string): Promise<AiSavedReport> {
  const res = await api.getClient().get<{ success: boolean; report: AiSavedReport }>(`/ai-agent/reports/${id}`);
  return res.data.report;
}

export async function createSavedReport(input: {
  reportName: string;
  description?: string | null;
  query?: string;
  intent?: string;
  filters?: Record<string, unknown>;
  displayColumns?: string[];
  sourceContext?: Record<string, unknown> | null;
  outputType?: string;
  schedule?: Record<string, unknown> | null;
}): Promise<AiSavedReport> {
  const res = await api.getClient().post<{ success: boolean; report: AiSavedReport }>('/ai-agent/reports', input);
  return res.data.report;
}

export async function updateSavedReport(
  id: string,
  patch: Partial<{
    reportName: string;
    description: string | null;
    query: string;
    intent: string;
    filters: Record<string, unknown>;
    displayColumns: string[];
    sourceContext: Record<string, unknown> | null;
    outputType: string;
    schedule: Record<string, unknown> | null;
  }>
): Promise<AiSavedReport> {
  const res = await api.getClient().patch<{ success: boolean; report: AiSavedReport }>(`/ai-agent/reports/${id}`, patch);
  return res.data.report;
}

export async function deleteSavedReport(id: string): Promise<void> {
  await api.getClient().delete(`/ai-agent/reports/${id}`);
}

export async function runSavedReport(id: string): Promise<AssistantResponseLike> {
  const res = await api.getClient().post<{ success: boolean; result: AssistantResponseLike }>(`/ai-agent/reports/${id}/run`);
  return res.data.result;
}

export async function fetchUdqIntents(): Promise<UdqIntentCatalogItem[]> {
  const res = await api.getClient().get<{ success: boolean; intents: UdqIntentCatalogItem[] }>('/ai-agent/udq/intents');
  return res.data.intents || [];
}

export async function previewUdq(input: {
  query?: string;
  intent?: string;
  filters?: Record<string, unknown>;
  displayColumns?: string[];
  sourceContext?: Record<string, unknown> | null;
}): Promise<{
  intent: string;
  filters: Record<string, unknown>;
  result: AssistantResponseLike;
  matchedFromNl: boolean;
}> {
  try {
    const res = await api.getClient().post<{
      success: boolean;
      intent: string;
      filters: Record<string, unknown>;
      result: AssistantResponseLike;
      matchedFromNl: boolean;
      message?: string;
    }>('/ai-agent/udq/preview', input);
    return {
      intent: res.data.intent,
      filters: res.data.filters,
      result: res.data.result,
      matchedFromNl: res.data.matchedFromNl,
    };
  } catch (e: unknown) {
    const ax = e as { response?: { data?: { message?: string } }; message?: string };
    const msg = ax.response?.data?.message || ax.message || 'Preview failed';
    throw new Error(msg);
  }
}

export async function createWorkspaceShare(input: {
  resourceType: 'conversation' | 'report' | 'dashboard' | 'insight';
  resourceId: string;
  sharedWithUserId: string;
  permission?: 'view' | 'comment' | 'edit';
}): Promise<void> {
  await api.getClient().post('/ai-agent/shares', input);
}

export type HomeKpiItem = {
  key: string;
  label: string;
  value: number | string;
  format?: 'number' | 'currency' | 'text';
};

export async function fetchWorkspaceHome(): Promise<{ kpis: HomeKpiItem[]; generatedAt: string }> {
  const res = await api.getClient().get<{ success: boolean; snapshot: { kpis: HomeKpiItem[]; generatedAt: string } }>(
    '/ai-agent/workspace/home'
  );
  return res.data.snapshot;
}

export type WorkspaceSearchResult = {
  type: 'conversation' | 'pin' | 'report';
  id: string;
  title: string;
  subtitle?: string;
};

export async function fetchWorkspaceSearch(q: string): Promise<WorkspaceSearchResult[]> {
  const res = await api.getClient().get<{ success: boolean; results: WorkspaceSearchResult[] }>(
    '/ai-agent/workspace/search',
    { params: { q } }
  );
  return res.data.results || [];
}

export type WorkspaceShare = {
  id: string;
  resourceType: string;
  resourceId: string;
  permission: string;
};

export async function fetchWorkspaceShares(resourceType?: string): Promise<WorkspaceShare[]> {
  const res = await api.getClient().get<{ success: boolean; shares: WorkspaceShare[] }>('/ai-agent/shares', {
    params: resourceType ? { resourceType } : undefined,
  });
  return res.data.shares || [];
}
